import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Users, FileText, FileSignature, TrendingUp, BadgeEuro, Percent } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { eur } from "@/lib/constants";
import api from "@/lib/api";

function KPI({ icon: Icon, label, value, gold }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border p-5 ${gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2 text-slate-500 text-sm">
        <Icon className={`h-4 w-4 ${gold ? "text-[#8a6d0f]" : "text-slate-400"}`} /> {label}
      </div>
      <p className={`mt-2 font-display text-2xl font-semibold ${gold ? "text-[#8a6d0f]" : "text-slate-900"}`}>{value}</p>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)).catch(() => {}); }, []);

  if (!a) return <DashboardLayout title="Analytics" admin><div className="text-slate-400">Caricamento…</div></DashboardLayout>;

  return (
    <DashboardLayout title="Analytics" admin>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={Users} label="Segnalatori" value={a.reporters} />
        <KPI icon={FileText} label="Segnalazioni" value={a.total} />
        <KPI icon={FileSignature} label="Incarichi acquisiti" value={a.incarichi} />
        <KPI icon={TrendingUp} label="Vendite" value={a.deals} />
        <KPI icon={Percent} label="Conversion rate" value={`${a.conversion}%`} />
        <KPI icon={BadgeEuro} label="Fatturato generato" value={eur(a.fatturato)} />
        <KPI icon={BadgeEuro} label="Merito fidelizz. maturato" value={eur(a.maturato)} gold />
        <KPI icon={BadgeEuro} label="Merito fidelizz. pagato" value={eur(a.pagato)} gold />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-6">Funnel di conversione</h2>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={a.funnel} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="stage" width={90} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {a.funnel.map((_, i) => <Cell key={i} fill={i === a.funnel.length - 1 ? "#D4AF37" : "#0A1128"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">Distribuzione stati</h2>
          <div className="space-y-2 max-h-80 overflow-auto">
            {a.status_dist.sort((x, y) => y.count - x.count).map((s) => (
              <div key={s.status} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{s.label}</span>
                <span className="font-semibold text-slate-900">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
